// Created on 2023-08-03
// Created by Vitaut Tryputsin
//
// Copyright (c) 2023 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { InteractiveObject } from "../interactive-object.js";
/**
 * This class allows to control the model with specified id in the Viewer.
 * It represents [[InteractiveContext]] + [[PropertyManager]] and [[ModelLoader]] wrapper.
 * The model can be existing or not and can be loaded from memory, file or URL.
 * The model can consists from one or many graphical objects which have the same id or starts from the provided id.
 * User should specify id explicitly at construction time.
 * Beware to use unique ids - loading multiple objects with the same id will result in logical issues!
 *
 * Usual lifetime of the object:
 * - Create an instance of class using id.
 * - Use existing model or load model from the given URL/memory/file;
 * - Show/hide/modify object in the Viewer (e.g. [[InteractiveMesh.visible]]);
 * - Remove object from the Viewer (object is unregistered from the Viewer and memory released) via [[InteractiveMesh.remove]];
 * Alternatively, you can use functions of [[OccViewer.InteractiveContext]] + [[OccViewer.ModelLoader]]
 * from [[OccViewer.interactiveContext]] and [[OccViewer.modelLoader]]
 */
export class InteractiveMesh extends InteractiveObject {
    /**
     * Constructor.
     * @param {OccViewer} theModule viewer object
     * @param {string?} theId id of object (if it is not provided - will be generated automatically)
     */
    constructor(theModule, theId) { super(theModule, theId); }
    /**
     * Clone
     * @return {InteractiveMesh} cloned object
     */
    clone() {
        let aCloneId = super._clone();
        return new InteractiveMesh(this._myViewer, aCloneId);
    }
    /** Get/set translation vector. */
    get position() {
        this._myViewer.propertyManager.object = this._myObjId;
        return this._myViewer.propertyManager.position;
    }
    /** Set translation vector. */
    set position(theValue) {
        this._myViewer.propertyManager.object = this._myObjId;
        this._myViewer.propertyManager.position = theValue;
    }
    /** Move Object by provided vector. */
    move(theVector) { this._myViewer.propertyManager.move(theVector); }
    /** Get/set rotation quaternion. */
    get rotation() {
        this._myViewer.propertyManager.object = this._myObjId;
        return this._myViewer.propertyManager.rotation;
    }
    /** Set rotation quaternion. */
    set rotation(theValue) {
        this._myViewer.propertyManager.object = this._myObjId;
        this._myViewer.propertyManager.rotation = theValue;
    }
    /** rotate object around given axis. */
    rotate(theAxis, theAngle) {
        this._myViewer.propertyManager.object = this._myObjId;
        this._myViewer.propertyManager.rotate(theAxis, theAngle);
    }
    /** rotate object around point by provided axis and angle. */
    rotatePoint(theAxis, thePoint, theAngle) {
        this._myViewer.propertyManager.object = this._myObjId;
        this._myViewer.propertyManager.rotatePoint(theAxis, thePoint, theAngle);
    }
    /** Scale factor. */
    get scale() {
        this._myViewer.propertyManager.object = this._myObjId;
        return this._myViewer.propertyManager.scale;
    }
    /** Scale factor. */
    set scale(theValue) {
        this._myViewer.propertyManager.object = this._myObjId;
        this._myViewer.propertyManager.scale = theValue;
    }
    /** Transformation matrix. */
    get transformation() {
        this._myViewer.propertyManager.object = this._myObjId;
        return this._myViewer.propertyManager.transformation;
    }
    /** Transformation matrix. */
    set transformation(theValue) {
        this._myViewer.propertyManager.object = this._myObjId;
        this._myViewer.propertyManager.transformation = theValue;
    }
    /** Has custom material or not. */
    get hasCustomMaterial() {
        this._myViewer.propertyManager.object = this._myObjId;
        return this._myViewer.propertyManager.hasCustomMaterial;
    }
    /** Has custom material or not. */
    set hasCustomMaterial(theValue) {
        this._myViewer.propertyManager.object = this._myObjId;
        this._myViewer.propertyManager.hasCustomMaterial = theValue;
    }
    /** Custom material metallic factor `0..1`. */
    get metallic() {
        this._myViewer.propertyManager.object = this._myObjId;
        return this._myViewer.propertyManager.metallic;
    }
    /** Custom material metallic factor `0..1`. */
    set metallic(theValue) {
        this._myViewer.propertyManager.object = this._myObjId;
        this._myViewer.propertyManager.metallic = theValue;
    }
    /** Custom material roughness factor `0..1`. */
    get roughness() {
        this._myViewer.propertyManager.object = this._myObjId;
        return this._myViewer.propertyManager.roughness;
    }
    /** Custom material roughness factor `0..1`. */
    set roughness(theValue) {
        this._myViewer.propertyManager.object = this._myObjId;
        this._myViewer.propertyManager.roughness = theValue;
    }
    /** Display mode. */
    get displayMode() {
        this._myViewer.propertyManager.object = this._myObjId;
        return this._myViewer.propertyManager.displayMode;
    }
    /** Display mode. */
    set displayMode(theValue) {
        this._myViewer.propertyManager.object = this._myObjId;
        this._myViewer.propertyManager.displayMode = theValue;
    }
    /** Get/set properties array of selected objects. */
    get properties() {
        this._myViewer.propertyManager.object = this._myObjId;
        return this._myViewer.propertyManager.properties;
    }
    /** Get/set properties array of selected objects. */
    set properties(theValue) {
        this._myViewer.propertyManager.object = this._myObjId;
        this._myViewer.propertyManager.properties = theValue;
    }
    /**
     * Open object from the given URL and register presentation(s) in the hidden (erased) state.
     * If Interactive Object was created with empty id, it will be generated from the given URL.
     *
     * The file will be loaded asynchronously.
     *
     * Depending on the file format, this method might read only a model structure
     * without geometry - see [[OccViewer.modelLoader]].
     * Warning! Displaying an object with not yet loaded geometry will produce errors!
     *
     * @param {string} theFileUrl the file URL address
     * @param {boolean} theToExpand expand model or display as a single part
     * @return {Promise<boolean>} FALSE if model cannot be opened
     */
    openUrlAsync(theFileUrl, theToExpand) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._myViewer.modelLoader.openFromUrl(this._myObjId, theFileUrl, theToExpand);
        });
    }
    /**
     * Open object from loaded file [[ModelLoader.preloadFile]] and register presentation(s) in erased state.
     * @param {string}  theFilePath path to the file in emulated filesystem
     * @param {boolean} theToExpand expand model or display as a single part
     * @return {boolean} FALSE on reading error
     */
    openFromFile(theFilePath, theToExpand) {
        return this._myViewer.modelLoader.openFromFile(this._myObjId, theFilePath, theToExpand);
    }
    /**
     * Open object from memory and register presentation(s) in erased state.
     * @param {ArrayBuffer} theBuffer   pointer to data
     * @param {boolean}     theToExpand expand model or display as a single part
     * @return {boolean} FALSE on reading error
     */
    openFromMemory(theBuffer, theToExpand) {
        return this._myViewer.modelLoader.openFromMemory(this._myObjId, theBuffer, theToExpand);
    }
    /**
     * Load deferred mesh data from file previously opened by [[InteractiveMesh.openUrlAsync]]
     * or by opening new specified URL.
     * This method load data without displaying.
     * @param {string?} theFileUrl the file URL address to read model from
     *                             if you want to download model before mesh loading
     * @param {boolean?} theToExpand expand model or display as a single part
     * @return {Promise<boolean>} FALSE if object was not found
     */
    loadData(theFileUrl, theToExpand) {
        return __awaiter(this, void 0, void 0, function* () {
            if (theFileUrl) {
                yield this.openUrlAsync(theFileUrl, theToExpand ? theToExpand : true);
            }
            return this._myViewer.interactiveContext.loadObjectListAsync(this._myObjId);
        });
    }
    /** Set custom color an specific group of triangles
     * @param {GroupColor[]} theGroupColors array of group of triangles and color
     * @param {boolean}      theToReplace flag that tell if current colorized triangles
     *                                    should be replaced.
     */
    setCustomColorTriangles(theGroupColors, theToReplace = true) {
        this._myViewer.interactiveContext.setCustomColorTriangles(this._myObjId, theGroupColors, theToReplace);
    }
    /** Remove custom color an specific group of triangles
     * @param {boolean} theToClear flag that tell if all the custom colored
     *                             triangles should be cleared, or only the ones
     *                             set by `setCustomColorTriangles` function
     */
    removeCustomColorTriangles(theToClearAll = true) {
        this._myViewer.interactiveContext.removeCustomColorTriangles(this._myObjId, theToClearAll);
    }
}
